import { debounce } from "lodash";
import PropTypes from "prop-types";
import { useState, useCallback, useRef } from "react";

import Dropdown, {
  DropdownTrigger,
} from "@javascript/reactComponents/lds/dropdown/Dropdown";
import DropdownItem from "@javascript/reactComponents/lds/dropdown/DropdownItem";
import DropdownList from "@javascript/reactComponents/lds/dropdown/DropdownList";
import Input from "@javascript/reactComponents/lds/Input";

import { getJson } from "@javascript/helpers/fetchUtilities";
import { highlightTermInItem } from "@javascript/helpers/stringHelpers";
import { aliasSearchIcon } from "../facetSearchUtils";

import {
  AUTOCOMPLETE_INITIAL_SUGGESTIONS,
  DEBOUNCE_TIMEOUT,
  ENTITY_TO_ICON_MAP,
  TYPE_TO_ICON_MAP,
} from "@javascript/constants/facet_search/autocomplete_constants";

// import mockAutocompleteData from "@javascript/helpers/mockAutocompleteData";

// TODO: remove this once we clean up the DB (#188859861)
const removeEmoji = (str) => {
  return str.replace(
    /(_ZAP Search|_Prebuilt Search|\u26a1|\u2b50|[\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2011-\u26FF]|\uD83E[\uDD10-\uDDFF])/g,
    ""
  );
};

export const typeIcon = (type) => {
  if (type === "alias_search") {
    return aliasSearchIcon(I18n.locale);
  } else {
    return TYPE_TO_ICON_MAP[type] || TYPE_TO_ICON_MAP["default"];
  }
};

export const entityIcon = (entityType) =>
  ENTITY_TO_ICON_MAP[entityType] || ENTITY_TO_ICON_MAP["default"];

export function FacetSearchAutocompleteItem({
  onDropdownItemSelected,
  onViewProfileSelected,
  responseItem,
  searchTerm,
  size,
}) {
  const { type, display, special_cases_type, entity_type, url } = responseItem;

  let label = highlightTermInItem(
    removeEmoji(display), // TODO: Go back to "display" when we clean up the DB (#188859861)
    searchTerm,
    `type-body-bold-${size}`
  );

  const ddItemProps = {
    ariaLabel: label,
    value: responseItem,
  };

  // Handle cases with label suffixes

  const actualType = special_cases_type || type;
  const suffixTypes = [
    "current_search",
    "recent_search",
    "source_doc_search",
    "starts_with_search",
  ];

  if (suffixTypes.includes(actualType)) {
    const i18nPath = `facet_search.autocomplete.${actualType}`;
    label += ` - <i>${I18n.t(i18nPath)}</i>`;
  }

  ddItemProps.label = <span dangerouslySetInnerHTML={{ __html: label }}></span>;

  // Handle entities
  if (type === "entity") {
    ddItemProps.leadingIcon = entityIcon(entity_type);
    ddItemProps.trailingIcon = "chevron-right";

    return (
      <Dropdown nested onItemSelected={onViewProfileSelected}>
        <DropdownTrigger>
          <DropdownItem
            {...ddItemProps}
            onItemSelected={onDropdownItemSelected}
          />
        </DropdownTrigger>
        <DropdownList>
          <DropdownItem
            href={url}
            hrefNewWindow
            label={I18n.t("facet_search.autocomplete.view_profile")}
            trailingIcon="link-external"
            value={responseItem}
          />
        </DropdownList>
      </Dropdown>
    );
  }

  ddItemProps.leadingIcon = typeIcon(type);

  return <DropdownItem {...ddItemProps} />;
}

function FacetSearchAutocomplete({
  className = "",
  id,
  inputId,
  onItemSelectedStoreAction = "autocomplete",
  size = "medium",
  source = "/autocomplete/search_autocomplete",
  value = "",
}) {
  const [dropdownItems, setDropdownItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);

  const inputRef = useRef(null);

  const getInitialSuggestions = (inputVal) => {
    const suggestions = AUTOCOMPLETE_INITIAL_SUGGESTIONS.map((suggestion) => {
      return {
        ...suggestion,
        matched: suggestion.matched?.replace(/PLACEHOLDER/g, inputVal),
        display: suggestion.display?.replace(/PLACEHOLDER/g, inputVal),
      };
    });
    return suggestions;
  };

  const triggerAutocomplete = useCallback(
    debounce(async (val) => {
      // Handle the case when input is empty
      if (val.length === 0) {
        setOpen(false);
        return;
      }

      let results = [];

      if (val.length < 3) {
        // Handle the case when input is 1 or 2 characters

        results = getInitialSuggestions(val);
      } else {
        // Handle the case when input is 3+ characters - fetch from API (or use mock data)
        // Use mock data instead of API call, uncomment import, above to access mockAutocompleteData
        // const responseJson = mockAutocompleteData;

        const searchParams = new URLSearchParams({ text: val }).toString();
        const responseJson = await getJson(`${source}?${searchParams}`);
        results = responseJson.results;
      }

      setDropdownItems(
        results.map((item) => (
          <FacetSearchAutocompleteItem
            key={`${item.matched}_${item.type}`}
            responseItem={item}
            searchTerm={val}
            size={size}
            onDropdownItemSelected={onDropdownItemSelected}
            onViewProfileSelected={onViewProfileSelected}
            inputRef={inputRef}
          />
        ))
      );

      setOpen(true);
    }, DEBOUNCE_TIMEOUT),
    []
  );

  const onInputValueChange = (val) => {
    setInputValue(val);
    triggerAutocomplete(val);
  };

  const onInputFocus = (val) => {
    // TODO: this is a patch. We should remove it once we integrate the
    // scattered query_box logic into this component (DX-120)
    setInputValue(val);
    triggerAutocomplete(val);
  };

  const onViewProfileSelected = (item) => {
    // Refocus on the input
    inputRef.current?.getElementsByTagName("input")[0].focus();

    // Track view profile click
    const { entity_type, id, official_name } = item.value;

    Zymewire.external_track_action("Autocomplete - View profile click", {
      entity_type_ref: entity_type,
      entity_name: official_name,
      entity_id: id,
    });
  };

  const onDropdownClose = () => {
    // This is hacky and disgusting but
    // a) timeout because if we check the activeElement immediately, it hasn't updated
    // b) activeElement is the only way to tell where the user's focus is

    window.setTimeout(() => {
      const input = inputRef.current?.getElementsByTagName("input")[0];

      if (document.activeElement !== input) {
        setOpen(false);
      }
    }, 50)
  };

  const handleSubmit = () => {
    setOpen(false);
  };

  const onDropdownItemSelected = (selectedItem) => {
    if (
      typeof FacetSearchStoreActions[onItemSelectedStoreAction] === "function"
    ) {
      const value = selectedItem.value;

      FacetSearchStoreActions[onItemSelectedStoreAction](value);

      const matchedTypes = [
        "entity",
        "recent_search",
        "source_doc_search",
        "special_cases",
        "text",
      ];

      const inputFieldValue = matchedTypes.includes(value.type)
        ? value.matched
        : "";

      setInputValue(inputFieldValue);

      setOpen(false);
    } else {
      console.warn(
        `Store action "${onItemSelectedStoreAction}" not found in FacetSearchStoreActions`
      );
    }
  };

  const maxDropdownWidth = inputRef.current
    ? inputRef.current.getBoundingClientRect().width + "px"
    : "unset";

  return (
    <div
      className={`facet-search-autocomplete-container ${className}`}
      data-testid={id}
      id={id}
    >
      <Input
        autoComplete="off"
        className="search-query"
        forceHideHelperText={true}
        id={inputId}
        leadingIcon="search"
        name="query"
        onValueChange={onInputValueChange}
        onFocus={onInputFocus}
        onSubmit={handleSubmit}
        placeholder={I18n.t("facet_search.autocomplete.query_placeholder")}
        ref={inputRef}
        role="searchbox"
        showLabel={false}
        size={size}
        width="100%"
        value={inputValue}
      />
      <Dropdown
        controlledOpen={open}
        dropdownClassName="facet-search-autocomplete-dropdown"
        maxWidth={maxDropdownWidth}
        onDropdownClose={onDropdownClose}
        onItemSelected={onDropdownItemSelected}
        size={size}
      >
        <DropdownList>{dropdownItems}</DropdownList>
      </Dropdown>
    </div>
  );
}

export const FacetSearchAutocompletePropTypes = {
  className: PropTypes.string,
  id: PropTypes.string.isRequired,
  inputId: PropTypes.string.isRequired,
  onItemSelectedStoreAction: PropTypes.string,
  size: PropTypes.oneOf(["small", "medium", "large"]),
  source: PropTypes.string,
  value: PropTypes.string,
};

FacetSearchAutocomplete.propTypes = FacetSearchAutocompletePropTypes;

window.FacetSearchAutocomplete = FacetSearchAutocomplete;
export default FacetSearchAutocomplete;
